import { BETR_PICKS_APP, BETR_PICKS_WEB, betrAppLink } from "@/lib/bet-links";

const PICK6_WEB = "https://pick6.draftkings.com/";

export type Pick6Link = { readonly sport: string; readonly pickGroup: string; readonly picks: readonly string[] };

const parsed = new Map<string, Pick6Link | null>();

export function parsePick6Link(link: string | null | undefined): Pick6Link | null {
  if (!link) return null;
  const held = parsed.get(link);
  if (held !== undefined) return held;
  let value: Pick6Link | null = null;
  const query = link.indexOf("?");
  if (query >= 0 && (link.startsWith(PICK6_WEB) || link.startsWith("dkpsx://"))) {
    const params = new URLSearchParams(link.slice(query + 1));
    const sport = params.get("sport") ?? "";
    const pickGroup = params.get("pickGroup") ?? "";
    const picks = (params.get("picks") ?? "").split(",").map((pick) => pick.trim()).filter((pick) => pick !== "");
    if (sport && pickGroup && picks.length > 0) value = { sport, pickGroup, picks };
  }
  parsed.set(link, value);
  return value;
}

export function pick6Group(link: string | null | undefined): string | null {
  return parsePick6Link(link)?.pickGroup ?? null;
}

export function pick6SlipLink(links: readonly (string | null | undefined)[]): string | null {
  if (links.length === 0) return null;
  const parts = links.map(parsePick6Link);
  const first = parts[0];
  if (first === null) return null;
  const picks: string[] = [];
  for (const part of parts) {
    if (part === null || part.sport !== first.sport || part.pickGroup !== first.pickGroup) return null;
    for (const pick of part.picks) if (!picks.includes(pick)) picks.push(pick);
  }
  const params = new URLSearchParams({ sport: first.sport, pickGroup: first.pickGroup, picks: picks.join(","), entrySource: "shareLink" });
  return `${PICK6_WEB}?${params.toString().replace(/%2C/g, ",")}`;
}

const PRIZEPICKS_WEB = "https://app.prizepicks.com/";

function prizePicksProjections(link: string | null | undefined): string[] | null {
  if (!link || !link.startsWith(PRIZEPICKS_WEB)) return null;
  const query = link.indexOf("?");
  if (query < 0) return null;
  const tokens = (new URLSearchParams(link.slice(query + 1)).get("projections") ?? "").split(",").map((token) => token.trim()).filter((token) => token !== "");
  return tokens.length > 0 ? tokens : null;
}

export function prizePicksSlipLink(links: readonly (string | null | undefined)[]): string | null {
  if (links.length === 0) return null;
  const tokens: string[] = [];
  for (const link of links) {
    const part = prizePicksProjections(link);
    if (part === null) return null;
    for (const token of part) if (!tokens.includes(token)) tokens.push(token);
  }
  return `${PRIZEPICKS_WEB}?projections=${tokens.join(",")}`;
}

const CHALKBOARD_ENTRY = "https://chalkboard-link.onelink.me/cUPZ/entryShare";
const CHALKBOARD_TAG = "cb-upsidetools";

export function parseChalkboardLink(link: string | null | undefined): string[] | null {
  if (!link || !link.startsWith(CHALKBOARD_ENTRY)) return null;
  const query = link.indexOf("?");
  if (query < 0) return null;
  const encoded = new URLSearchParams(link.slice(query + 1)).get("markets");
  if (!encoded) return null;
  let payload: string;
  try {
    payload = atob(encoded);
  } catch {
    return null;
  }
  const markets = payload.split(",").map((market) => market.trim()).filter((market) => market !== "");
  return markets.length > 0 ? markets : null;
}

export function chalkboardSlipLink(links: readonly (string | null | undefined)[]): string | null {
  if (links.length === 0) return null;
  const markets: string[] = [];
  for (const link of links) {
    const part = parseChalkboardLink(link);
    if (part === null) return null;
    for (const market of part) if (!markets.includes(market)) markets.push(market);
  }
  return `${CHALKBOARD_ENTRY}?s=${CHALKBOARD_TAG}&markets=${encodeURIComponent(btoa(markets.join(",")))}`;
}

const DRAFTKINGS_EVENT = "https://sportsbook.draftkings.com/event/";

function draftKingsOutcomes(link: string | null | undefined): { event: string; outcomes: string[] } | null {
  if (!link || !link.startsWith(DRAFTKINGS_EVENT)) return null;
  const query = link.indexOf("?outcomes=");
  if (query < 0) return null;
  const outcomes = link.slice(query + "?outcomes=".length).split(/%2B|\+/i).map((outcome) => outcome.trim()).filter((outcome) => outcome !== "");
  return outcomes.length > 0 ? { event: link.slice(0, query), outcomes } : null;
}

export function draftKingsSlipLink(links: readonly (string | null | undefined)[]): string | null {
  if (links.length === 0) return null;
  let event: string | null = null;
  const outcomes: string[] = [];
  for (const link of links) {
    const part = draftKingsOutcomes(link);
    if (part === null) return null;
    event ??= part.event;
    for (const outcome of part.outcomes) if (!outcomes.includes(outcome)) outcomes.push(outcome);
  }
  return `${event}?outcomes=${outcomes.join("%2B")}`;
}

const BETMGM_SPORTS = "https://www.nj.betmgm.com/en/sports";

function betMgmOptions(link: string | null | undefined): string[] | null {
  if (!link || !link.startsWith(BETMGM_SPORTS + "?")) return null;
  const options = (new URLSearchParams(link.slice(link.indexOf("?") + 1)).get("options") ?? "").split(",").map((option) => option.trim()).filter((option) => option !== "");
  return options.length > 0 ? options : null;
}

export function betMgmSlipLink(links: readonly (string | null | undefined)[]): string | null {
  if (links.length === 0) return null;
  const options: string[] = [];
  for (const link of links) {
    const part = betMgmOptions(link);
    if (part === null) return null;
    for (const option of part) if (!options.includes(option)) options.push(option);
  }
  return `${BETMGM_SPORTS}?options=${options.join(",")}&type=${options.length > 1 ? "Multi" : "Single"}`;
}

const BETRIVERS_EVENT = /^(https:\/\/[a-z]+\.betrivers\.com\/\?page=sportsbook#event\/\d+)\?coupon=[^#]*?(?:%7C|\|)(\d+(?:,\d+)*)(?:%7C|\|)/i;

export function betRiversSlipLink(links: readonly (string | null | undefined)[]): string | null {
  if (links.length === 0) return null;
  let event: string | null = null;
  const outcomes: string[] = [];
  for (const link of links) {
    const match = link ? BETRIVERS_EVENT.exec(link) : null;
    if (match === null) return null;
    event ??= match[1];
    for (const outcome of match[2].split(",")) if (!outcomes.includes(outcome)) outcomes.push(outcome);
  }
  return `${event}?coupon=${outcomes.length > 1 ? "combination" : "single"}%7C${outcomes.join(",")}%7C`;
}

const HARDROCK_BETSLIP = /betslip\/(\d+(?:,\d+)*)/;
const HARDROCK_SHARE = "https://share.hardrock.bet/Pt0T/bet?deep_link_value=hardrock://betslip/";

export function hardRockSlipLink(links: readonly (string | null | undefined)[]): string | null {
  if (links.length === 0) return null;
  const ids: string[] = [];
  for (const link of links) {
    const match = link && (link.startsWith("https://app.hardrock.bet/") || link.startsWith(HARDROCK_SHARE)) ? HARDROCK_BETSLIP.exec(link) : null;
    if (match === null) return null;
    for (const id of match[1].split(",")) if (!ids.includes(id)) ids.push(id);
  }
  return HARDROCK_SHARE + ids.join(",");
}

const CAESARS_WEB = /^(https:\/\/sportsbook\.caesars\.com\/us\/[a-z]{2}\/bet\/betslip)\?selectionIds=([^&]+)/;
const CAESARS_APP = "https://czr.onelink.me/t5iS?deep_link_value=betslip&deep_link_sub1=";

export function caesarsSlipLink(links: readonly (string | null | undefined)[]): string | null {
  if (links.length === 0) return null;
  let base: string | null = null;
  let app: boolean | null = null;
  const ids: string[] = [];
  for (const link of links) {
    if (!link) return null;
    const web = CAESARS_WEB.exec(link);
    const isApp = web === null && link.startsWith(CAESARS_APP);
    if (web === null && !isApp) return null;
    if (app !== null && app !== isApp) return null;
    app = isApp;
    base ??= isApp ? CAESARS_APP : web![1] + "?selectionIds=";
    const raw = isApp ? link.slice(CAESARS_APP.length) : web![2];
    for (const id of decodeURIComponent(raw).split(",")) if (id && !ids.includes(id)) ids.push(id);
  }
  return base === null || ids.length === 0 ? null : base + ids.join(",");
}

const FANATICS_APP = "https://fanatics.onelink.me/5kut?deep_link_sub1=";
const FANATICS_SUFFIX = "&deep_link_value=consume-betslip";

type FanaticsLeg = { eventId: string; marketId: string; selectionId: string };

function fanaticsLegs(link: string | null | undefined): FanaticsLeg[] | null {
  if (!link || !link.startsWith(FANATICS_APP) || !link.endsWith(FANATICS_SUFFIX)) return null;
  try {
    const parsed = JSON.parse(decodeURIComponent(link.slice(FANATICS_APP.length, -FANATICS_SUFFIX.length))) as { legs?: FanaticsLeg[] };
    const legs = (parsed.legs ?? []).filter((leg) => leg && leg.eventId && leg.marketId && leg.selectionId);
    return legs.length > 0 ? legs.map(({ eventId, marketId, selectionId }) => ({ eventId: String(eventId), marketId: String(marketId), selectionId: String(selectionId) })) : null;
  } catch {
    return null;
  }
}

export function fanaticsSlipLink(links: readonly (string | null | undefined)[]): string | null {
  if (links.length === 0) return null;
  const legs: FanaticsLeg[] = [];
  for (const link of links) {
    const part = fanaticsLegs(link);
    if (part === null) return null;
    for (const leg of part) if (!legs.some((held) => held.selectionId === leg.selectionId)) legs.push(leg);
  }
  return FANATICS_APP + encodeURIComponent(JSON.stringify({ legs })) + FANATICS_SUFFIX;
}

const FANDUEL_LINK = /^(https:\/\/(?:[A-Za-z]{2}\.)?sportsbook\.fanduel\.com\/addToBetslip|fanduelsportsbook:\/\/account\.sportsbook\.fanduel\.com\/sportsbook\/addToBetslip)\?(.+)$/;

export function fanDuelSlipLink(links: readonly (string | null | undefined)[]): string | null {
  if (links.length === 0) return null;
  let base: string | null = null;
  let app: boolean | null = null;
  const pairs: string[] = [];
  for (const link of links) {
    const match = link ? FANDUEL_LINK.exec(link) : null;
    if (match === null) return null;
    const isApp = match[1].startsWith("fanduelsportsbook://");
    if (app !== null && app !== isApp) return null;
    app = isApp;
    base ??= match[1];
    const params = new URLSearchParams(match[2]);
    const markets = params.getAll("marketId");
    const selections = params.getAll("selectionId");
    if (markets.length === 0 || markets.length !== selections.length) return null;
    for (let i = 0; i < markets.length; i++) {
      const pair = `marketId=${markets[i]}&selectionId=${selections[i]}`;
      if (!pairs.includes(pair)) pairs.push(pair);
    }
  }
  return base === null ? null : `${base}?${pairs.join("&")}`;
}

const THESCORE_LINK = /^(https:\/\/(?:sportsbook\.thescore\.bet\/sport\/[^?]+|app\.thescore\.bet\/a\/[^?]+))\?(.+)$/;

type TheScoreLeg = {
  readonly base: string;
  readonly app: boolean;
  readonly canonical: string | null;
  readonly fallbackBase: string | null;
  readonly selection: string;
  readonly numerator: string;
  readonly denominator: string;
};

function parseTheScoreLink(link: string | null | undefined): TheScoreLeg | null {
  const match = link ? THESCORE_LINK.exec(link) : null;
  if (match === null) return null;
  const params = new URLSearchParams(match[2]);
  const selection = params.get("market_selection_id[0]");
  const numerator = params.get("odds_numerator[0]");
  const denominator = params.get("odds_denominator[0]");
  if (!selection || !numerator || !denominator) return null;
  const app = match[1].startsWith("https://app.");
  const canonical = params.get("$canonical_url");
  const fallback = params.get("$fallback_url");
  if (app && (!canonical || !fallback)) return null;
  return { base: match[1], app, canonical, fallbackBase: fallback ? fallback.split("?")[0] : null, selection, numerator, denominator };
}

function theScoreQuery(legs: readonly TheScoreLeg[]): URLSearchParams {
  const params = new URLSearchParams();
  legs.forEach((leg, i) => {
    params.append(`market_selection_id[${i}]`, leg.selection);
    params.append(`odds_numerator[${i}]`, leg.numerator);
    params.append(`odds_denominator[${i}]`, leg.denominator);
  });
  return params;
}

export function theScoreSlipLink(links: readonly (string | null | undefined)[]): string | null {
  if (links.length === 0) return null;
  const legs: TheScoreLeg[] = [];
  for (const link of links) {
    const leg = parseTheScoreLink(link);
    if (leg === null) return null;
    if (!legs.some((seen) => seen.selection === leg.selection)) legs.push(leg);
  }
  const [first] = legs;
  if (legs.some((leg) => leg.app !== first.app)) return null;
  if (legs.length === 1) return links.find((link) => link != null) ?? null;
  const web = theScoreQuery(legs);
  if (!first.app) return `${first.base}?${web.toString()}`;
  const params = new URLSearchParams();
  params.set("$canonical_url", first.canonical ?? "");
  params.set("$fallback_url", `${first.fallbackBase}?${web.toString()}`);
  for (const [key, value] of web) params.append(key, value);
  return `${first.base}?${params.toString()}`;
}

const COURTSIDE_SHARE = "https://api.courtside.app/api/share?picks=";
const COURTSIDE_REF = "&ref=UPSIDE";
const COURTSIDE_LINK = /^https:\/\/api\.courtside\.app\/api\/share\?picks=([^&]+)(?:&ref=[^&]*)?$/;
const SLEEPER_ENTRY = "https://sleeper.onelink.me/GF91?deep_link_value=entry_prefill&deep_link_sub1=";
const SLEEPER_LINK = /^https:\/\/sleeper\.onelink\.me\/GF91\?deep_link_value=entry_prefill&deep_link_sub1=([^&]+)&af_dp=sleeper:\/\/$/;

function joinedIds(links: readonly (string | null | undefined)[], pattern: RegExp): string[] | null {
  if (links.length === 0) return null;
  const ids: string[] = [];
  for (const link of links) {
    const match = link ? pattern.exec(link) : null;
    if (match === null) return null;
    for (const id of decodeURIComponent(match[1]).split(",")) {
      if (id !== "" && !ids.includes(id)) ids.push(id);
    }
  }
  return ids.length === 0 ? null : ids;
}

export function courtsidePickIds(links: readonly (string | null | undefined)[]): string[] | null {
  return joinedIds(links, COURTSIDE_LINK);
}

export function courtsideSlipLink(links: readonly (string | null | undefined)[]): string | null {
  const ids = courtsidePickIds(links);
  return ids === null ? null : COURTSIDE_SHARE + encodeURIComponent(ids.join(",")) + COURTSIDE_REF;
}

export function sleeperSlipLink(links: readonly (string | null | undefined)[]): string | null {
  const ids = joinedIds(links, SLEEPER_LINK);
  return ids === null ? null : `${SLEEPER_ENTRY}${ids.join(",")}&af_dp=sleeper://`;
}

type BetrLeg = { readonly picks: readonly string[]; readonly partner: string | null; readonly app: boolean };

function parseBetrLink(link: string | null | undefined): BetrLeg | null {
  if (!link) return null;
  const app = link.startsWith(BETR_PICKS_APP) ? link : betrAppLink(link);
  if (app === null) return null;
  const params = new URLSearchParams(app.slice(BETR_PICKS_APP.length));
  const encoded = params.get("picks");
  if (!encoded) return null;
  let payload: string;
  try {
    payload = atob(encoded);
  } catch {
    return null;
  }
  const picks = payload.split(",").map((pick) => pick.trim()).filter((pick) => pick !== "");
  return picks.length > 0 ? { picks, partner: params.get("partner"), app: app === link } : null;
}

export function betrSlipLink(links: readonly (string | null | undefined)[]): string | null {
  if (links.length === 0) return null;
  const legs = links.map(parseBetrLink);
  const first = legs[0];
  if (first === null) return null;
  const picks: string[] = [];
  for (const leg of legs) {
    if (leg === null || leg.app !== first.app) return null;
    for (const pick of leg.picks) if (!picks.includes(pick)) picks.push(pick);
  }
  const inner = `${BETR_PICKS_APP}picks=${encodeURIComponent(btoa(picks.join(",")))}${first.partner ? `&partner=${encodeURIComponent(first.partner)}` : ""}`;
  return first.app ? inner : BETR_PICKS_WEB + encodeURIComponent(inner);
}

export function slipLink(book: string, links: readonly (string | null | undefined)[]): string | null {
  if (book === "dkpick6") return pick6SlipLink(links);
  if (book === "prizepicks") return prizePicksSlipLink(links);
  if (book === "chalkboardpicks" || book === "chalkboardssp") return chalkboardSlipLink(links);
  if (book === "draftkings") return draftKingsSlipLink(links);
  if (book === "betmgm") return betMgmSlipLink(links);
  if (book === "kambi" || book === "betrivers") return betRiversSlipLink(links);
  if (book === "hardrock") return hardRockSlipLink(links);
  if (book === "caesars") return caesarsSlipLink(links);
  if (book === "fanatics") return fanaticsSlipLink(links);
  if (book === "fanduel") return fanDuelSlipLink(links);
  if (book === "thescore") return theScoreSlipLink(links);
  if (book === "courtside") return courtsideSlipLink(links);
  if (book === "sleeper") return sleeperSlipLink(links);
  if (book === "betr") return betrSlipLink(links);
  return null;
}

export function slipScope(book: string, link: string | null | undefined, league: string): string | null {
  if (book === "dkpick6") return pick6Group(link) ?? league;
  return null;
}
